export const USER_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  RECRUITER: 'recruiter',
  HIRING_MANAGER: 'hiring_manager',
  MEMBER: 'member',
} as const

export type UserRoleKey = keyof typeof USER_ROLES
export type UserRoleValue = (typeof USER_ROLES)[UserRoleKey]

export const ROLE_HIERARCHY: Record<UserRoleValue, number> = {
  owner: 100,
  admin: 80,
  recruiter: 60,
  hiring_manager: 40,
  member: 20,
}

export const PERMISSIONS = {
  // Tenant
  TENANT_MANAGE: 'tenant:manage',
  TENANT_VIEW: 'tenant:view',

  // Users
  USERS_MANAGE: 'users:manage',
  USERS_INVITE: 'users:invite',
  USERS_VIEW: 'users:view',

  // Jobs
  JOBS_CREATE: 'jobs:create',
  JOBS_EDIT: 'jobs:edit',
  JOBS_DELETE: 'jobs:delete',
  JOBS_PUBLISH: 'jobs:publish',
  JOBS_VIEW: 'jobs:view',

  // Candidates
  CANDIDATES_CREATE: 'candidates:create',
  CANDIDATES_EDIT: 'candidates:edit',
  CANDIDATES_DELETE: 'candidates:delete',
  CANDIDATES_VIEW: 'candidates:view',

  // Applications
  APPLICATIONS_MANAGE: 'applications:manage',
  APPLICATIONS_MOVE: 'applications:move',
  APPLICATIONS_VIEW: 'applications:view',

  // Interviews
  INTERVIEWS_SCHEDULE: 'interviews:schedule',
  INTERVIEWS_MANAGE: 'interviews:manage',
  INTERVIEWS_VIEW: 'interviews:view',

  // Evaluations
  EVALUATIONS_CREATE: 'evaluations:create',
  EVALUATIONS_VIEW: 'evaluations:view',
  EVALUATIONS_VIEW_PRIVATE: 'evaluations:view_private',

  // Notes
  NOTES_CREATE: 'notes:create',

  // Email
  EMAIL_SEND: 'email:send',
  EMAIL_TEMPLATES_MANAGE: 'email:templates:manage',

  // Reports
  REPORTS_VIEW: 'reports:view',

  // Career Page
  CAREER_PAGE_MANAGE: 'career_page:manage',
} as const

export type PermissionKey = keyof typeof PERMISSIONS
export type PermissionValue = (typeof PERMISSIONS)[PermissionKey]

// Default permissions by role
export const ROLE_PERMISSIONS: Record<UserRoleValue, PermissionValue[]> = {
  owner: Object.values(PERMISSIONS),
  admin: Object.values(PERMISSIONS).filter(
    (p) => p !== PERMISSIONS.TENANT_MANAGE
  ),
  recruiter: [
    PERMISSIONS.TENANT_VIEW,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.JOBS_CREATE,
    PERMISSIONS.JOBS_EDIT,
    PERMISSIONS.JOBS_PUBLISH,
    PERMISSIONS.JOBS_VIEW,
    PERMISSIONS.CANDIDATES_CREATE,
    PERMISSIONS.CANDIDATES_EDIT,
    PERMISSIONS.CANDIDATES_VIEW,
    PERMISSIONS.APPLICATIONS_MANAGE,
    PERMISSIONS.APPLICATIONS_MOVE,
    PERMISSIONS.APPLICATIONS_VIEW,
    PERMISSIONS.INTERVIEWS_SCHEDULE,
    PERMISSIONS.INTERVIEWS_MANAGE,
    PERMISSIONS.INTERVIEWS_VIEW,
    PERMISSIONS.EVALUATIONS_CREATE,
    PERMISSIONS.EVALUATIONS_VIEW,
    PERMISSIONS.NOTES_CREATE,
    PERMISSIONS.EMAIL_SEND,
    PERMISSIONS.REPORTS_VIEW,
  ],
  hiring_manager: [
    PERMISSIONS.TENANT_VIEW,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.JOBS_VIEW,
    PERMISSIONS.CANDIDATES_VIEW,
    PERMISSIONS.APPLICATIONS_MOVE,
    PERMISSIONS.APPLICATIONS_VIEW,
    PERMISSIONS.INTERVIEWS_VIEW,
    PERMISSIONS.EVALUATIONS_CREATE,
    PERMISSIONS.EVALUATIONS_VIEW,
    PERMISSIONS.NOTES_CREATE,
  ],
  member: [
    PERMISSIONS.TENANT_VIEW,
    PERMISSIONS.JOBS_VIEW,
    PERMISSIONS.INTERVIEWS_VIEW,
    PERMISSIONS.EVALUATIONS_CREATE,
    PERMISSIONS.EVALUATIONS_VIEW,
    PERMISSIONS.NOTES_CREATE,
  ],
}

export function hasPermission(
  userRole: UserRoleValue,
  userPermissions: string[],
  requiredPermission: PermissionValue
): boolean {
  // Check explicit permissions first
  if (userPermissions.includes(requiredPermission)) {
    return true
  }

  // Check role-based permissions
  const rolePermissions = ROLE_PERMISSIONS[userRole] || []
  return rolePermissions.includes(requiredPermission)
}

export function hasRole(
  userRole: UserRoleValue,
  requiredRole: UserRoleValue
): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole]
}
